// Helper di permessi (RBAC) per il menu laterale — la configurazione (MENU,
// PAGE_ACTIONS, PAGE_PATH) resta nel menu.js di ogni modulo, questa fabbrica
// fornisce solo le funzioni, sullo stesso principio di createLanguageEngine.

// Ordina le sezioni secondo l'ordine scelto per il ruolo ("Menu per ruolo"): prima quelle
// elencate in `order` (chiavi sezione), poi le altre nella posizione di default.
export function orderSections(sections, order) {
  if (!Array.isArray(order) || order.length === 0) return sections;
  const rank = new Map(order.map((key, i) => [key, i]));
  return sections
    .map((section, index) => ({ section, index }))
    .sort((a, b) => {
      const ra = rank.has(a.section.key) ? rank.get(a.section.key) : Infinity;
      const rb = rank.has(b.section.key) ? rank.get(b.section.key) : Infinity;
      if (ra !== rb) return ra === Infinity ? 1 : rb === Infinity ? -1 : ra - rb;
      return a.index - b.index;
    })
    .map(({ section }) => section);
}

// options (opzionali, per-modulo):
//   strictActions  sessione senza array "actions" = nessun permesso (default: tutto consentito)
//   hideForbidden  le voci senza permesso spariscono dal menu invece di essere "disabled"
//   pageMenuKey    mappa pagina -> chiave voce di menu: la pagina è accessibile solo se la voce è spuntata in "Menu per ruolo"
export function createMenuHelpers(MENU, PAGE_ACTIONS, PAGE_PATH, extraItemFilter = () => true, options = {}) {
  const { strictActions = false, hideForbidden = false, pageMenuKey = null } = options;

  const canAction = (user, action) => {
    if (!action) return true;
    const actions = user?.actions;
    if (!Array.isArray(actions)) return !strictActions; // retro-compat: sessione senza permessi
    if (Array.isArray(action)) return action.some((a) => actions.includes(a));
    return actions.includes(action);
  };

  const isMenuItemChecked = (user, item) => {
    const menu = user?.menu;
    if (!Array.isArray(menu)) return true; // retro-compat: sessione senza configurazione menu
    return menu.includes(item.key);
  };

  // Una voce compare nel menu se è spuntata in "Menu per ruolo", indipendentemente
  // dal permesso; se manca l'azione corrispondente in "Permessi" viene marcata
  // disabled (visibile ma non cliccabile) invece di essere nascosta del tutto.
  // extraItemFilter (opzionale, per-modulo) può nascondere ulteriori voci in base
  // a condizioni specifiche del modulo (es. ruolo Supporto).
  const getVisibleSections = (user) =>
    orderSections(MENU, user?.menu_order).map((section) => ({
      ...section,
      items: section.items
        .filter((item) => isMenuItemChecked(user, item))
        .filter((item) => extraItemFilter(item, user))
        .filter((item) => !hideForbidden || canAction(user, item.action))
        .map((item) => ({ ...item, disabled: !canAction(user, item.action) })),
    })).filter((section) => section.items.length > 0);

  const canAccessPage = (user, page) => {
    if (!canAction(user, PAGE_ACTIONS[page])) return false;
    const key = pageMenuKey && pageMenuKey[page];
    return !key || isMenuItemChecked(user, { key });
  };

  const getDefaultPath = (user) => {
    const page = user?.default_page || 'dashboard';
    if (canAccessPage(user, page) && PAGE_PATH[page]) {
      return PAGE_PATH[page];
    }
    const sections = getVisibleSections(user);
    for (const section of sections) {
      for (const item of section.items) {
        if (item.path && !item.disabled) return item.path;
      }
    }
    return '/dashboard';
  };

  return { canAction, isMenuItemChecked, getVisibleSections, canAccessPage, getDefaultPath };
}
